import { getGlobal } from './global.js';
import {} from './i-console.js';
import {} from './i-env-console.js';
import {} from './i-goto-logger-config.js';
import { isIGotoLoggerMessage } from './i-goto-logger-message.js';
import { compareLogCategories, LOG_CATEGORIES } from './log-category.js';
import { NullLogger } from './null-logger.js';
const sequence = (() => {
    let start = 0;
    function nextSequenceNumber() {
        const ret = start;
        start = start + 1 > start ? start + 1 : 0;
        return ret;
    }
    return nextSequenceNumber;
})();
function mergeContexts(contexts) {
    const ret = {};
    contexts.reverse().forEach((context) => {
        context.forEach((value, key) => {
            ret[key] = value;
        });
    });
    return ret;
}
function isObject(obj) {
    return typeof obj === 'object' && obj !== null && obj.length === undefined;
}
export class GotoLogger {
    constructor(_originNamespaces = []) {
        this._originNamespaces = _originNamespaces;
        this._rootConsole = this._decideRootConsole();
        this._context = new Map();
        this._targetConsole = this._rootConsole;
        this._listeners = {};
        this.logToConsole = GotoLogger._config.logToConsole;
        this._setupConsoles();
    }
    static createRoot(config) {
        const targetLogger = new GotoLogger();
        targetLogger.configure(config);
        const rootLogger = new GotoLogger();
        rootLogger.setTarget(targetLogger);
        // redirect methods to target logger, which receives messages from all loggers
        rootLogger.subscribe = (...args) => targetLogger.subscribe(...args);
        return rootLogger;
    }
    debug(message, ...optionalParams) {
        this._trace('debug', [this._originNamespaces.join('.'), message, ...optionalParams]);
    }
    error(message, ...optionalParams) {
        this._trace('error', [this._originNamespaces.join('.'), message, ...optionalParams]);
    }
    log(message, ...optionalParams) {
        this._trace('log', [this._originNamespaces.join('.'), message, ...optionalParams]);
    }
    info(message, ...optionalParams) {
        this._trace('info', [this._originNamespaces.join('.'), message, ...optionalParams]);
    }
    warn(message, ...optionalParams) {
        this._trace('warn', [this._originNamespaces.join('.'), message, ...optionalParams]);
    }
    /**
     * The standard logging functions such as `log`,`info`,`warn` etc. only provide the ability
     * to log messages with optional data parameters. The other meta data such as timestamp,
     * sequence numbers and such are generated by the GotoLogger itself. However, this may not be
     * desirable if the target logger is another GotoLogger. So the metadata properties of the
     * target logger can be overwritten by using this function with the provided message.
     *
     * Optionally you can also pass an `IGotoLoggerMessage` without the `seq` number property. In
     * that case the GotoLogger will generate one for that particular message.
     * @param message complete typed log message
     */
    logMessage(message) {
        this._trace(message.category, message);
    }
    bind(...args) {
        return this.create(args);
    }
    create(namespace) {
        const logger = new GotoLogger(this._originNamespaces.concat(namespace));
        logger.setParent(this);
        logger.setTarget(this._targetConsole);
        return logger;
    }
    setParent(newParent) {
        this._parent = newParent;
    }
    setContext(property, value) {
        this._context.set(property, value);
    }
    deleteContext(property) {
        if (this._context.has(property)) {
            this._context.delete(property);
        }
    }
    getContext(prop) {
        let value = undefined;
        if (prop === undefined) {
            return this._getFullNodeContext();
        }
        if (this._context.has(prop)) {
            value = this._context.get(prop);
        }
        else if (this._parent) {
            value = this._parent.getContext(prop);
        }
        return value;
    }
    setTarget(targetConsole) {
        this._targetConsole = targetConsole;
    }
    getLocalLogRecord() {
        return GotoLogger._localLogRecord ? GotoLogger._localLogRecord : [];
    }
    subscribe(category, handler) {
        var _a;
        this._listeners[category] = this._listeners[category] || [];
        (_a = this._listeners[category]) === null || _a === void 0 ? void 0 : _a.push(handler);
    }
    configure(options) {
        if (options) {
            GotoLogger._config = Object.assign(Object.assign({}, GotoLogger._config), options);
            if (typeof options.localLogRecordSize === 'number') {
                if (options.localLogRecordSize === 0) {
                    this._deactivateLocalLogRecording();
                }
                else if (options.localLogRecordSize > 0) {
                    this._activateLocalLogRecording();
                }
            }
            if (this.logToConsole !== GotoLogger._config.logToConsole) {
                this._setupConsoles();
            }
            // ensure we have a valid mincategory
            GotoLogger._config.mincategory = LOG_CATEGORIES.includes(GotoLogger._config.mincategory)
                ? GotoLogger._config.mincategory
                : LOG_CATEGORIES[0];
        }
        return GotoLogger._config;
    }
    _getFullNodeContext() {
        const contexts = [];
        let currentNode = this;
        while (currentNode) {
            contexts.push(currentNode._context);
            currentNode = currentNode._parent;
        }
        return mergeContexts(contexts);
    }
    _activateLocalLogRecording() {
        GotoLogger._localLogRecord = [];
        GotoLogger._localLogRecordIdx = 0;
    }
    _deactivateLocalLogRecording() {
        GotoLogger._localLogRecord = null;
    }
    _addMessageToStaticLogRecords(message) {
        if (GotoLogger._localLogRecord) {
            GotoLogger._localLogRecord[GotoLogger._localLogRecordIdx] = message;
            GotoLogger._localLogRecordIdx++;
            if (GotoLogger._localLogRecordIdx === GotoLogger._config.localLogRecordSize) {
                GotoLogger._localLogRecordIdx = 0;
            }
        }
    }
    _publishLogToSubscribers(category, ...args) {
        for (const handler of this._listeners[category] || []) {
            try {
                handler.apply(null, args);
            }
            catch (_a) {
                // ignore
            }
        }
    }
    _trace(category, data) {
        let message;
        // if data is type any[], then we have to build the `IGotoLoggerMessage`
        // else if data is not an array, then it must be an IGotoLoggerMessage and we just re-use that
        if (Array.isArray(data)) {
            message = {
                ts: new Date().toISOString(),
                seq: sequence(),
                category,
                origin: '',
                data,
            };
            if (data.length > 1 && typeof data[0] === 'string') {
                // assume first data element is the origin
                message.origin = data.shift();
            }
            const destructureError = (value) => {
                if (value instanceof Error) {
                    return {
                        errorMessage: value.message,
                        errorName: value.name,
                        errorStack: value.stack,
                    };
                }
                return value;
            };
            const ensureErrorContent = (value) => {
                if (value instanceof Error) {
                    return destructureError(value);
                }
                else if (Array.isArray(value)) {
                    return value.map(ensureErrorContent);
                }
                else if (isObject(value)) {
                    return Object.fromEntries(Object.entries(value).map(([key, childValue]) => [
                        key,
                        // No recursion on purpose: Consider large objects or cyclic objects
                        destructureError(childValue),
                    ]));
                }
                else {
                    return value;
                }
            };
            message.data = message.data.map(ensureErrorContent);
            this._addMessageToStaticLogRecords(message);
        }
        else if (isIGotoLoggerMessage(data)) {
            message = data;
        }
        else {
            // if it is neither an array nor a full IGotoLoggerMessage, then it means that
            // the `seq` property is missing, so we have to add it
            message = {
                ts: data.ts,
                seq: sequence(),
                category: data.category,
                origin: data.origin,
                data: data.data,
            };
        }
        if (category === 'log' || compareLogCategories(category, this.configure().mincategory)) {
            this._passthru(message);
        }
        // prevent a malicious subscriber to modify the original message for other subsequent subscribers
        Object.freeze(message);
        this._publishLogToSubscribers(category, message);
    }
    _passthru(message) {
        const target = this._targetConsole || this._rootConsole;
        if (target instanceof GotoLogger) {
            target.logMessage(message);
        }
        else {
            const args = [message.ts, '[' + message.category + ']'];
            if (message.origin) {
                args.push(message.origin);
            }
            if (message.data) {
                if (Array.isArray(message.data)) {
                    args.push(...message.data);
                }
                else {
                    args.push(message.data || message);
                }
            }
            const func = target[message.category] || target.log;
            func.apply(target, args);
        }
    }
    _decideRootConsole() {
        var _a, _b;
        const storage = (_a = globalThis === null || globalThis === void 0 ? void 0 : globalThis.localStorage) === null || _a === void 0 ? void 0 : _a.getItem('gotoLoggerSettings');
        try {
            const settings = storage
                ? JSON.parse(storage)
                : undefined;
            if (settings &&
                typeof (settings === null || settings === void 0 ? void 0 : settings.logToConsole) === 'boolean' &&
                (settings === null || settings === void 0 ? void 0 : settings.logToConsole) !== this.logToConsole) {
                this.logToConsole = settings === null || settings === void 0 ? void 0 : settings.logToConsole;
            }
        }
        catch (error) {
            (_b = globalThis === null || globalThis === void 0 ? void 0 : globalThis.localStorage) === null || _b === void 0 ? void 0 : _b.removeItem('gotoLoggerSettings');
        }
        return this.logToConsole === true ? getGlobal().console : new NullLogger();
    }
    _setupConsoles() {
        this._rootConsole = this._decideRootConsole();
        this._targetConsole = this._rootConsole;
    }
}
GotoLogger._localLogRecord = null;
GotoLogger._localLogRecordIdx = 0;
GotoLogger._config = {
    localLogRecordSize: 0,
    mincategory: 'error',
    logToConsole: true,
};
//# sourceMappingURL=goto-logger.js.map